import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, PlusCircle, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { runTest } from '@/services/evaluation/run-test';
import { Test, TestResult } from '@/types/evals-types';
import { getTests } from '@/services/evaluation/get-tests';
import {
  getTestResults,
  extractRunIdFromS3Key,
} from '@/services/evaluation/get-test-results';
import { useParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import TestResultsTable from '../components/test-results';
import { v4 as uuidv4 } from 'uuid';
import { deleteTest } from '@/services/evaluation/delete-test';
import { useNotification } from '@/components/notifications';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { sortByUpdatedAt } from '@/utils/sort-utils';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useEvaluationStore } from '@/stores/evaluation-store';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { useTestRunStatusPolling } from '@/hooks/use-test-status-polling';

export const TestsConfigView = () => {
  const { testId: selectedTestId } = useParams();

  const [tests, setTests] = useState<Test[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isLoadingTestResults, setIsLoadingTestResults] = useState(false);
  const [pollingTestId, setPollingTestId] = useState<string | null>(null);
  const previousTestIdRef = useRef<string | null>(null);
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const [isLoadingTests, setIsLoadingTests] = useState(true);
  const { currentOrg } = useOrgStore();
  const [previousOrgId, setPreviousOrgId] = useState<string | null>(null);
  const { setTests: setEvaluationStoreTests, removeTest } =
    useEvaluationStore();

  const { pollTestRunStatus, clearPolling } = useTestRunStatusPolling();

  // Reset test selection when organization changes
  useEffect(() => {
    if (currentOrg) {
      const orgHasChanged =
        previousOrgId !== null && previousOrgId !== currentOrg.orgid;

      if (orgHasChanged) {
        // Reset test selection and form state
        setTestResults([]);
        // Clear polling state
        setPollingTestId(null);
        clearPolling();
      }

      setPreviousOrgId(currentOrg.orgid);
    }
  }, [currentOrg, previousOrgId, clearPolling]);

  // Handle re-running an existing test
  const handleRerunTest = async (testId: string) => {
    // Keep old results visible but set loading state to show indicator
    setIsLoadingTestResults(true);

    try {
      // Start the test run and get S3 key
      const runResponse = await runTest(testId);
      const s3Key = runResponse.s3_keys[0];

      if (!s3Key) {
        notification.error('Failed to start test run');
        setIsLoadingTestResults(false);
        return;
      }

      // Extract runId from S3 key
      const runId = extractRunIdFromS3Key(s3Key);

      // Set polling state to show loading on button
      setPollingTestId(testId);

      // Start polling for test completion
      notification.success('Test started! Results will appear once ready.');

      pollTestRunStatus(testId, runId, {
        onSuccess: async () => {
          try {
            const testResults = await getTestResults(testId);
            setTestResults(testResults);
            notification.success('Test completed successfully!');
          } catch (resultsError) {
            console.error(
              'Error fetching test results after completion:',
              resultsError,
            );
            notification.error('Failed to fetch test results after completion');
          } finally {
            setPollingTestId(null);
            setIsLoadingTestResults(false);
          }
        },
        onError: (errorMessage) => {
          notification.error(errorMessage);
          setPollingTestId(null);
          setIsLoadingTestResults(false);
        },
      });
    } catch (error) {
      console.error('Error running test:', error);
      notification.error('Failed to start test run');
      setPollingTestId(null);
      setIsLoadingTestResults(false);
    }
  };

  // Get the selected test if any
  const selectedTest = selectedTestId
    ? tests.find((t) => t.id === selectedTestId)
    : null;

  useEffect(() => {
    // Fetch tests and their results on mount
    const fetchTestsAndResults = async () => {
      setIsLoadingTests(true);
      try {
        const testSummaries = await getTests();
        // Sort tests by updated_at in descending order (most recent first)
        const sortedTests = sortByUpdatedAt<Test>(testSummaries);
        setTests(sortedTests);
        setEvaluationStoreTests(sortedTests);
      } catch (error) {
        console.error('Error fetching tests and results', error);
        setTests([]); // fallback to empty
      } finally {
        setIsLoadingTests(false);
      }
    };
    fetchTestsAndResults();
  }, [setEvaluationStoreTests]);

  useEffect(() => {
    if (!selectedTestId) {
      setTestResults([]);
      setIsLoadingTestResults(false);
      previousTestIdRef.current = null;
      return;
    }

    // Only clear results when switching to a different test
    const isDifferentTest = previousTestIdRef.current !== selectedTestId;

    if (isDifferentTest) {
      setTestResults([]);
      previousTestIdRef.current = selectedTestId;
    }
    setIsLoadingTestResults(true);

    const getTestResult = async () => {
      try {
        const testResults = await getTestResults(selectedTestId);
        setTestResults(testResults as TestResult[]);
      } catch (error) {
        console.error('Error fetching test results:', error);
        setTestResults([]);
      } finally {
        setIsLoadingTestResults(false);
      }
    };
    getTestResult();
  }, [selectedTestId]);

  const handleDeleteTest = async (testId: string) => {
    if (!testId) return;

    setIsDeleteLoading(true);
    try {
      await deleteTest(testId);

      // Update local state
      setTests((prev) => prev?.filter((test) => test.id !== testId));
      removeTest(testId);

      // If we're deleting the currently selected test, reset selection
      if (selectedTestId === testId) {
        setTestResults([]);
      }

      notification.success('Test deleted successfully');
      navigate(ROUTES.EVALUATION.TESTS);
    } catch (error) {
      console.error('Error deleting test:', error);
      notification.error('Failed to delete test');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleCreateNewTest = () => {
    const newTest: Test = {
      id: uuidv4(),
      name: 'New Test',
      created_at: new Date(),
      is_new: true,
      evaluations: [],
      test_type: 'content_test',
      test_config: { contents: [] },
    };
    setTests((prev) => [newTest, ...(prev || [])]);
  };

  return (
    <div className='w-full'>
      <div className='p-6'>
        {isLoadingTests ? (
          <div className='flex items-center justify-center h-full'>
            <LoadingSpinner />
          </div>
        ) : tests.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full text-center'>
            <h2 className='text-xl font-semibold mb-2'>No Tests Yet</h2>
            <p className='text-muted-foreground mb-6'>
              Get started by creating your first test.
            </p>
            <Button onClick={handleCreateNewTest}>
              <PlusCircle className='size-4' />
              New Test
            </Button>
          </div>
        ) : (
          <>
            <div className='flex justify-between items-center mb-6'>
              <h2 className='text-3xl font-bold tracking-tight'>
                {selectedTest?.name}
              </h2>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon'>
                    <MoreVertical className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => setIsDeleteModalOpen(true)}
                    className='text-destructive focus:text-destructive'
                  >
                    <Trash2 className='size-4 text-destructive' />
                    Delete Test
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {isLoadingTestResults && testResults.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-12'>
                <LoadingSpinner />
                <p className='mt-4 text-muted-foreground'>
                  Loading test results... This may take a few moments.
                </p>
              </div>
            ) : (
              <>
                {isLoadingTestResults && testResults.length > 0 && (
                  <div className='mb-4 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3'>
                    <LoadingSpinner />
                    <div>
                      <p className='font-medium text-blue-900 dark:text-blue-100'>
                        New test run in progress...
                      </p>
                      <p className='text-sm text-blue-700 dark:text-blue-300'>
                        Displaying previous results. New results will appear
                        when the test completes.
                      </p>
                    </div>
                  </div>
                )}
                <TestResultsTable
                  testResults={testResults as TestResult[]}
                  onRerun={handleRerunTest}
                  pollingTestId={pollingTestId}
                />
              </>
            )}
          </>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        title='Delete Test'
        description='Are you sure you want to delete this test? All test results and data will be permanently removed. This action cannot be undone.'
        buttonLabel='Delete Test'
        handleDelete={() => selectedTestId && handleDeleteTest(selectedTestId)}
        handleCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};
